import type {
    ProteinAtomSite,
    ProteinBond,
    ProteinSecondaryStructureRange,
    ProteinStructure,
} from '@/adapters/biology/_contract/proteinStructure';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/biology/_contract/config';
import { biologyStructureEntry, type BiologyStructureEntry } from '@/adapters/biology/_data/structures';
import { normalizeProteinStructure } from '@/adapters/biology/_pure/normalizeProteinStructure';

const DEFAULT_PARSE_ID = 'mmCIF';

type CifField = {
    str: (row: number) => string;
    float: (row: number) => number;
    int: (row: number) => number;
};

type CifCategory = {
    rowCount: number;
    getField: (name: string) => CifField | undefined;
};

function readField(
    category: CifCategory,
    ...names: string[]
): CifField | undefined {
    for (const name of names) {
        const field = category.getField(name);
        if (field) return field;
    }
    return undefined;
}

function readSecondaryStructureRanges(
    category: CifCategory | undefined,
    type: ProteinSecondaryStructureRange['type'],
    categoryName: string,
    typeFieldName?: string,
): ProteinSecondaryStructureRange[] {
    if (!category) return [];

    const chainId = readField(category, 'beg_label_asym_id');
    const startSequence = readField(category, 'beg_label_seq_id');
    const endChainId = readField(category, 'end_label_asym_id');
    const endSequence = readField(category, 'end_label_seq_id');
    const structureType = typeFieldName ? readField(category, typeFieldName) : undefined;

    if (!chainId || !startSequence || !endChainId || !endSequence || (typeFieldName && !structureType)) {
        throw new Error(`The mmCIF category "${categoryName}" is missing required secondary-structure fields.`);
    }

    const ranges: ProteinSecondaryStructureRange[] = [];
    for (let row = 0; row < category.rowCount; row += 1) {
        if (structureType && !structureType.str(row).startsWith('HELX')) continue;
        const start = startSequence.int(row);
        const end = endSequence.int(row);
        const chain = chainId.str(row);
        if (!Number.isFinite(start) || !Number.isFinite(end) || start > end
            || !chain || chain === '.' || chain === '?'
            || endChainId.str(row) !== chain) continue;

        ranges.push({ chainId: chain, startSequence: start, endSequence: end, type });
    }
    return ranges;
}

export async function parseProteinStructureText(
    text: string,
    id = DEFAULT_PARSE_ID,
): Promise<ProteinStructure> {
    const { CIF } = await import('molstar/lib/mol-io/reader/cif.js');
    const result = await CIF.parse(text).run();
    if (result.isError) {
        throw new Error(`Could not parse mmCIF structure "${id}": ${result.message}`);
    }

    const block = result.result.blocks[0];
    if (!block?.categories.atom_site) {
        throw new Error(`The mmCIF structure "${id}" has no atom_site category.`);
    }

    const [{ trajectoryFromMmCIF }, { Structure }, { Unit }, { BondType }] = await Promise.all([
        import('molstar/lib/mol-model-formats/structure/mmcif.js'),
        import('molstar/lib/mol-model/structure/structure/structure.js'),
        import('molstar/lib/mol-model/structure/structure/unit.js'),
        import('molstar/lib/mol-model/structure/model/types.js'),
    ]);
    const trajectory = await trajectoryFromMmCIF(block).run();
    if (trajectory.frameCount === 0) throw new Error(`The mmCIF structure "${id}" has no models.`);
    const model = trajectory.representative;
    const molecularStructure = Structure.ofModel(model);
    const hierarchy = model.atomicHierarchy;
    const conformation = model.atomicConformation;
    if (!conformation.xyzDefined) {
        throw new Error(`The mmCIF structure "${id}" is missing atomic coordinates.`);
    }
    const atomSites: ProteinAtomSite[] = [];

    const atomId = (index: number) => `${model.modelNum}:${conformation.atomId.value(index)}`;
    for (let index = 0; index < hierarchy.atoms._rowCount; index += 1) {
        const residue = hierarchy.residueAtomSegments.index[index];
        const chain = hierarchy.chainAtomSegments.index[index];
        if (hierarchy.residues.group_PDB.value(residue) !== 'ATOM') continue;
        const position: [number, number, number] = [
            conformation.x[index], conformation.y[index], conformation.z[index],
        ];
        if (!position.every(Number.isFinite)) {
            throw new Error(`The mmCIF structure "${id}" has invalid coordinates for atom ${atomId(index)}.`);
        }
        const alternate = hierarchy.atoms.label_alt_id.value(index);
        atomSites.push({
            id: atomId(index),
            element: hierarchy.atoms.type_symbol.value(index),
            modelNumber: model.modelNum,
            recordKind: 'ATOM',
            atomName: hierarchy.atoms.label_atom_id.value(index),
            chainId: hierarchy.chains.label_asym_id.value(chain),
            residueSequence: hierarchy.residues.label_seq_id.value(residue),
            residueName: hierarchy.atoms.label_comp_id.value(index),
            ...(alternate ? { alternateLocation: alternate } : {}),
            occupancy: conformation.occupancy.value(index),
            position,
        });
    }

    const bonds: ProteinBond[] = [];
    const bondIndexByKey = new Map<string, number>();
    const aromaticFlag = BondType.fromName('aromatic');
    const addBond = (a: number, b: number, order: number, flags: number) => {
        const atomIdA = atomId(Math.min(a, b));
        const atomIdB = atomId(Math.max(a, b));
        const key = `${atomIdA}/${atomIdB}`;
        const existingIndex = bondIndexByKey.get(key);
        const aromatic = BondType.is(flags, aromaticFlag);
        if (existingIndex === undefined) {
            bondIndexByKey.set(key, bonds.length);
            bonds.push({ atomIdA, atomIdB, order, aromatic });
        } else if (aromatic && !bonds[existingIndex]!.aromatic) {
            bonds[existingIndex] = { ...bonds[existingIndex]!, aromatic: true };
        }
    };
    for (const unit of molecularStructure.units) {
        if (!Unit.isAtomic(unit)) continue;
        const graph = unit.bonds;
        for (let edge = 0; edge < graph.a.length; edge += 1) {
            if (graph.a[edge] >= graph.b[edge] || !BondType.isCovalent(graph.edgeProps.flags[edge])) continue;
            addBond(
                unit.elements[graph.a[edge]],
                unit.elements[graph.b[edge]],
                graph.edgeProps.order[edge],
                graph.edgeProps.flags[edge],
            );
        }
    }
    for (const edge of molecularStructure.interUnitBonds.edges) {
        const unitA = molecularStructure.unitMap.get(edge.unitA);
        const unitB = molecularStructure.unitMap.get(edge.unitB);
        if (!Unit.isAtomic(unitA) || !Unit.isAtomic(unitB) || !BondType.isCovalent(edge.props.flag)) continue;
        addBond(
            unitA.elements[edge.indexA],
            unitB.elements[edge.indexB],
            edge.props.order,
            edge.props.flag,
        );
    }

    const secondaryStructureRanges = [
        ...readSecondaryStructureRanges(
            block?.categories.struct_conf,
            'helix',
            '_struct_conf',
            'conf_type_id',
        ),
        ...readSecondaryStructureRanges(
            block?.categories.struct_sheet_range,
            'sheet',
            '_struct_sheet_range',
        ),
    ];

    return normalizeProteinStructure(id, atomSites, secondaryStructureRanges, bonds);
}

export async function loadProteinStructure(
    entry: BiologyStructureEntry,
    signal: AbortSignal,
): Promise<ProteinStructure> {
    const response = await fetch(entry.url, { signal });
    if (!response.ok) {
        throw new Error(`Could not load mmCIF structure "${entry.id}": HTTP ${response.status}.`);
    }

    const structure = await parseProteinStructureText(await response.text(), entry.id);
    if (signal.aborted) throw new DOMException('Structure loading was aborted.', 'AbortError');
    return structure;
}

type StructureLoaderArgs = { config: Config; runtimeState: RuntimeState; cosmosData: CosmosDataModel };

/** Runtime effect owner that fetches and parses the structure picked in the overlay. */
export function createStructureLoader() {
    let controller: AbortController | undefined;
    let requestedId: string | undefined;
    const owner = ({ config, runtimeState, cosmosData }: StructureLoaderArgs) => {
        const id = runtimeState.structureId;
        if (runtimeState.loadedStructure?.structure.id === id || cosmosData.proteinStructure?.id === id) {
            requestedId = undefined;
            return;
        }
        if (requestedId === id) return;
        const entry = biologyStructureEntry(id);
        const update = config.runtimeCallbacks?.onRuntimeStateChange;
        if (!entry || !update) return;
        controller?.abort();
        const active = new AbortController();
        controller = active;
        requestedId = id;
        update((current) => ({ ...current, structureLoad: { id, status: 'loading' } }));
        const started = performance.now();
        loadProteinStructure(entry, active.signal).then((structure) => {
            if (active.signal.aborted) return;
            const loadMilliseconds = Math.round(performance.now() - started);
            update((current) => current.structureId !== id ? current : {
                ...current,
                loadedStructure: { structure, loadMilliseconds },
                structureLoad: undefined,
            });
        }).catch((error: unknown) => {
            if (active.signal.aborted) return;
            const message = error instanceof Error ? error.message : String(error);
            console.error('[biology] Structure load failed:', error);
            update((current) => current.structureId !== id ? current : {
                ...current,
                structureLoad: { id, status: 'error', message },
            });
        });
    };
    owner.dispose = () => {
        controller?.abort();
        controller = undefined;
        requestedId = undefined;
    };
    return owner;
}
